import * as XLSX from 'xlsx';
import { ImportPreviewContact, ColumnMapping } from '@openmirror/shared';

export interface ParsedFileData {
  headers: string[];
  rows: string[][];
  detectedMapping?: ColumnMapping;
}

export interface FileParseResult {
  success: boolean;
  data?: ParsedFileData;
  error?: string;
}

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Supported file types for contact import
 */
export const SUPPORTED_FILE_TYPES = {
  CSV: 'text/csv',
  EXCEL_XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  EXCEL_XLS: 'application/vnd.ms-excel'
} as const;

/**
 * Validate if the uploaded file is supported
 */
export function validateFileType(file: File): FileValidationResult {
  const supportedTypes = Object.values(SUPPORTED_FILE_TYPES);
  const fileExtension = file.name.toLowerCase().split('.').pop();
  
  // Check MIME type first
  if (supportedTypes.includes(file.type as any)) {
    return { isValid: true };
  }
  
  // Fallback to extension check for cases where MIME type might not be set correctly
  if (fileExtension === 'csv' || fileExtension === 'xlsx' || fileExtension === 'xls') {
    return { isValid: true };
  }
  
  return {
    isValid: false,
    error: 'Unsupported file type. Please upload a CSV (.csv) or Excel (.xlsx, .xls) file.'
  };
}

/**
 * Validate file size (max 10MB)
 */
export function validateFileSize(file: File): FileValidationResult {
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size too large. Please upload a file smaller than 10MB.'
    };
  }
  
  return { isValid: true };
}

/**
 * Parse CSV file using native File API
 */
export async function parseCSVFile(file: File): Promise<FileParseResult> {
  try {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length === 0) {
      return {
        success: false,
        error: 'The CSV file appears to be empty.'
      };
    }
    
    // Parse CSV with basic comma separation (handles quoted fields)
    const rows: string[][] = [];
    
    for (const line of lines) {
      const row = parseCSVLine(line);
      rows.push(row);
    }
    
    if (rows.length === 0) {
      return {
        success: false,
        error: 'No valid data found in the CSV file.'
      };
    }
    
    const headers = rows[0];
    const dataRows = rows.slice(1);
    
    const detectedMapping = detectColumnMapping(headers);
    
    return {
      success: true,
      data: {
        headers,
        rows: dataRows,
        detectedMapping
      }
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to parse CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Parse a single CSV line handling quoted fields and commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add the last field
  result.push(current.trim());
  
  return result;
}

/**
 * Parse Excel file using xlsx library
 */
export async function parseExcelFile(file: File): Promise<FileParseResult> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    // Use the first worksheet
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      return {
        success: false,
        error: 'The Excel file does not contain any worksheets.'
      };
    }
    
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Convert to array of arrays
    const rawData: any[][] = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1, 
      defval: '',
      blankrows: false 
    });
    
    if (rawData.length === 0) {
      return {
        success: false,
        error: 'The Excel file appears to be empty.'
      };
    }
    
    // Convert all values to strings and filter out completely empty rows
    const rows: string[][] = rawData
      .map(row => row.map(cell => String(cell || '').trim()))
      .filter(row => row.some(cell => cell !== ''));
    
    if (rows.length === 0) {
      return {
        success: false,
        error: 'No valid data found in the Excel file.'
      };
    }
    
    const headers = rows[0];
    const dataRows = rows.slice(1);
    
    const detectedMapping = detectColumnMapping(headers);
    
    return {
      success: true,
      data: {
        headers,
        rows: dataRows,
        detectedMapping
      }
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Main file parser function that handles both CSV and Excel files
 */
export async function parseFile(file: File): Promise<FileParseResult> {
  // Validate file type
  const typeValidation = validateFileType(file);
  if (!typeValidation.isValid) {
    return {
      success: false,
      error: typeValidation.error
    };
  }
  
  // Validate file size
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.isValid) {
    return {
      success: false,
      error: sizeValidation.error
    };
  }
  
  // Parse based on file type
  const fileExtension = file.name.toLowerCase().split('.').pop();
  
  if (fileExtension === 'csv' || file.type === SUPPORTED_FILE_TYPES.CSV) {
    return parseCSVFile(file);
  } else if (
    fileExtension === 'xlsx' || 
    fileExtension === 'xls' || 
    file.type === SUPPORTED_FILE_TYPES.EXCEL_XLSX || 
    file.type === SUPPORTED_FILE_TYPES.EXCEL_XLS
  ) {
    return parseExcelFile(file);
  }
  
  return {
    success: false,
    error: 'Unsupported file format.'
  };
}

/**
 * Detect column mapping based on header names
 */
export function detectColumnMapping(headers: string[]): ColumnMapping | undefined {
  if (!headers || headers.length === 0) {
    return undefined;
  }
  
  const namePatterns = /^(.*)(name|full.?name|contact.?name|first.?name|last.?name)(.*)$/i;
  const phonePatterns = /^(.*)(phone|number|mobile|cell|telephone|contact.?number|phone.?number)(.*)$/i;
  const categoryPatterns = /^(category|group|type|tag|label)$/i;
  
  let nameColumn = -1;
  let phoneColumn = -1;
  let categoryColumn = -1;
  
  headers.forEach((header, index) => {
    const cleanHeader = header.trim();
    
    if (namePatterns.test(cleanHeader) && nameColumn === -1) {
      nameColumn = index;
    } else if (phonePatterns.test(cleanHeader) && phoneColumn === -1) {
      phoneColumn = index;
    } else if (categoryPatterns.test(cleanHeader) && categoryColumn === -1) {
      categoryColumn = index;
    }
  });
  
  // Only return mapping if we found at least name and phone columns
  if (nameColumn >= 0 && phoneColumn >= 0) {
    const mapping: ColumnMapping = {
      nameColumn,
      phoneColumn
    };
    
    if (categoryColumn >= 0) {
      mapping.categoryColumn = categoryColumn;
    }
    
    return mapping;
  }
  
  return undefined;
}

/**
 * Convert parsed file data to preview contacts using column mapping
 */
export function convertToPreviewContacts(
  data: ParsedFileData,
  mapping: ColumnMapping
): ImportPreviewContact[] {
  const { rows } = data;
  const { nameColumn, phoneColumn, categoryColumn } = mapping;
  
  return rows.map((row, index) => {
    const name = row[nameColumn]?.trim() || '';
    const number = row[phoneColumn]?.trim() || '';
    const category = categoryColumn !== undefined ? row[categoryColumn]?.trim() || '' : '';
    
    const validationErrors: string[] = [];
    
    // Basic validation
    if (!name) {
      validationErrors.push('Name is required');
    }
    
    if (!number) {
      validationErrors.push('Phone number is required');
    }
    
    return {
      name,
      number,
      isValid: validationErrors.length === 0,
      validationErrors,
      originalRowIndex: index + 1 // +1 because we exclude header row
    };
  });
}